import { PhraseInput, Text } from "@vericus/cadmus-ui";

import { useAppDispatch, useAppSelector } from "@/data/hooks";
import { selectAnswerSnapshot, setLocalSnapshot } from "@/features/authority";
import { QuestionFieldFragment } from "@/generated/graphql";
import { AnswerBlanks, Snapshot } from "@/stores/snapshot";

import { DropdownChoices } from "../dropdown-choices";
import * as styles from "./answer-form.css";

interface Props {
  /** Answer ID */
  answerBlockId: string;
  questionFields: QuestionFieldFragment[];
  /** Whether the input is readonly */
  isReadonly?: boolean;
}

/**
 * Answer response form for Fill in the Blanks with blanks to fill in.
 */
export function BlanksResponseForm(props: Props) {
  const { answerBlockId, questionFields, isReadonly } = props;

  const fieldIdentifiers = questionFields.map((field) => field.identifier);
  const snapshot = useAppSelector((state) =>
    selectAnswerSnapshot(state, answerBlockId)
  );
  const dispatch = useAppDispatch();

  const answerBlanks = getAnswerBlanks(snapshot);
  const version = snapshot?.version ?? 0;

  // Callback to set a single blank
  const setBlank = (identifier: string, value: string) => {
    // Update all the blanks together, even the ones that are untouched.
    // This is to ensure that we know there are blanks which are not yet
    // answered when just looking at the snapshot (without knowing all the
    // blanks)
    const updatedBlanks: Record<string, string> = {};
    const updatedFields: Record<string, string[]> = {};
    for (const bid of fieldIdentifiers) {
      if (bid === identifier) {
        updatedBlanks[bid] = value;
        updatedFields[bid] = [value];
      } else {
        updatedBlanks[bid] = answerBlanks[bid] ?? "";
        updatedFields[bid] = [answerBlanks[bid] ?? ""];
      }
    }

    dispatch(
      setLocalSnapshot({
        answerBlockId,
        snapshot: {
          version: version + 1,
          answerBlanks: updatedBlanks,
          answerFields: updatedFields,
        },
      })
    );
  };

  return (
    <div className={styles.response}>
      <div className={styles.fieldsContainer}>
        {questionFields.map(({ identifier, interaction }, index) => (
          <div key={identifier} className={styles.blankWrapper}>
            <div className={styles.label}>
              <Text kind="bodyMd" color="neutralBlack300">
                {`${index + 1}.`}
              </Text>
            </div>
            {interaction.__typename === "ChoiceInteraction" ? (
              <DropdownChoices
                choices={interaction.choices}
                onSelectChoice={(choiceId: string) => {
                  setBlank(identifier, choiceId);
                }}
                selectedChoiceId={answerBlanks[identifier] ?? null}
                width={313}
                isReadonly={isReadonly}
                seed={answerBlockId}
                shuffle={true}
                hasSeparator={true}
                placeholder="Select an answer"
                className={styles.blankInput}
              />
            ) : (
              <PhraseInput
                className={styles.blankInput}
                charactersLimit={50}
                alertLimit={10}
                placeholder="Add your answer"
                value={answerBlanks[identifier] ?? ""}
                onChange={(e) => {
                  setBlank(identifier, e.target.value);
                }}
                hideLimit={(answerBlanks[identifier] ?? "") === ""}
                disabled={isReadonly}
                aria-disabled={isReadonly}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function getAnswerBlanks(snapshot: Snapshot | undefined): AnswerBlanks {
  if (snapshot?.answerFields) {
    const blanks: AnswerBlanks = {};
    for (const identifier in snapshot.answerFields) {
      blanks[identifier] = snapshot.answerFields[identifier]?.[0] ?? "";
    }
  }
  return snapshot?.answerBlanks ?? {};
}
